import { useAuthStore } from '@/stores/auth';
import type { TokenPair } from 'shared';

const API_URL = import.meta.env.VITE_API_URL || '';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

// ApiValidationError is thrown when the server responds with HTTP 422.
// It carries the structured field-level errors so forms can display them inline.
export class ApiValidationError extends Error {
  readonly errors: Record<string, string>;
  constructor(message: string, errors: Record<string, string>) {
    super(message);
    this.name = 'ApiValidationError';
    this.errors = errors;
  }
}

// ApiConflictError is thrown when the server responds with HTTP 409.
// It optionally carries a machine-readable code for branching in UI.
export class ApiConflictError extends Error {
  readonly code?: string;
  // The response body, for the endpoints that say more than a code.
  readonly body?: unknown;
  constructor(message: string, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiConflictError';
    this.code = code;
    this.body = body;
  }
}

// ApiRequestError is thrown for every other failed response. It keeps the
// status and the body, so that a screen can tell the failures apart by the
// API's stable `error` code rather than by its English message.
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The API's stable code for a failure, or null. Some endpoints put it in
 * `error` ("AlreadyCheckedIn"), some in `code` ("BADGE_IN_USE"); the generic
 * ones ("Not Found") are HTTP status text and are not codes.
 */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiConflictError) && !(err instanceof ApiRequestError)) return null;
  const body = err.body;
  if (!body || typeof body !== 'object') return null;
  const { code, error } = body as { code?: unknown; error?: unknown };
  if (typeof code === 'string' && code) return code;
  return typeof error === 'string' && error ? error : null;
}

class ApiClient {
  private refreshPromise: Promise<TokenPair> | null = null;

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;
    const { accessToken, refreshToken, setTokens, logout } = useAuthStore.getState();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (!skipAuth && accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    // Handle token refresh on 401
    if (response.status === 401 && refreshToken && !skipAuth) {
      try {
        const tokens = await this.refreshTokens(refreshToken);
        setTokens(tokens);

        // Retry with new token
        (headers as Record<string, string>)['Authorization'] = `Bearer ${tokens.accessToken}`;
        response = await fetch(`${API_URL}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
      } catch {
        logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: 'An error occurred' }));

      // 422 Unprocessable Entity — structured validation errors
      if (response.status === 422 && body.errors) {
        throw new ApiValidationError(body.message ?? 'Validation failed', body.errors);
      }

      // 409 Conflict — business rule violation (e.g. badge in use by equipment)
      if (response.status === 409) {
        throw new ApiConflictError(body.message ?? 'Conflict', body.code, body);
      }

      throw new ApiRequestError(body.message || `HTTP ${response.status}`, response.status, body);
    }

    // Handle empty responses
    const text = await response.text();
    return text ? JSON.parse(text) : (undefined as T);
  }

  private async refreshTokens(refreshToken: string): Promise<TokenPair> {
    // Deduplicate concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Route refresh through the kind-specific endpoint so participant tokens
    // hit /api/auth/participant/refresh. Legacy tokens (no kind set) default
    // to the staff alias at /api/auth/refresh for backward compatibility.
    const { kind } = useAuthStore.getState();
    const endpoint = kind ? `/api/auth/${kind}/refresh` : '/api/auth/refresh';

    this.refreshPromise = this.request<TokenPair>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      skipAuth: true,
    }).finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  // Convenience methods
  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const api = new ApiClient();
